import React, { useMemo, useState } from 'react';
import { AopLink, Card, InternalCard, Pill, StatCard, WarnRow } from '../ui';
import {
	account,
	exitIntelligence,
	PURCHASE_SIGNALS,
	DEFAULT_PURCHASE_SIGNALS,
	computePurchaseProbability,
} from '../copData';
import { useCop } from '../CopApp';

const { stats, aggressivenessPresets, exitParams, alwaysOnSafeguards, purchaseSummary, diagnostics, engine } = exitIntelligence;

function hourLabel(h) {
	if (h === 0) return '12am';
	if (h < 12) return `${h}am`;
	if (h === 12) return '12pm';
	return `${h - 12}pm`;
}

function formatSignalValue(sig, value) {
	switch (sig.format) {
		case 'money':
			return `$${Number(value).toFixed(2)}`;
		case 'seconds':
			return `${value}s`;
		case 'hour':
			return hourLabel(Number(value));
		case 'yesno':
			return value ? 'Yes' : 'No';
		case 'detected':
			return value ? 'Detected' : 'None';
		case 'int':
			return String(value);
		case 'option':
			return (sig.options.find((o) => o.value === value) || {}).label || value;
		default:
			return String(value);
	}
}

// One interactive row: label + live impact, with the control that drives it.
function SignalRow({ sig, value, impact, tone, onChange }) {
	const label = sig.label || sig.attribute;
	return (
		<div className="cop-exit-signal">
			<div className="cop-exit-signal-head">
				<span className="cop-exit-signal-label">{label}</span>
				<span className="cop-exit-signal-value cop-num">{formatSignalValue(sig, value)}</span>
				<span className={`cop-exit-signal-impact cop-num tone-${tone}`}>{impact}</span>
			</div>
			<div className="cop-exit-signal-control">
				{sig.control === 'toggle' && (
					<button
						className={`cop-toggle${value ? ' on' : ''}`}
						onClick={() => onChange(!value)}
						aria-label={`toggle ${label}`}
					/>
				)}
				{sig.control === 'slider' && (
					<input
						type="range"
						min={sig.min}
						max={sig.max}
						step={sig.step}
						value={value}
						onChange={(e) => onChange(Number(e.target.value))}
						aria-label={label}
					/>
				)}
				{sig.control === 'select' && (
					<select className="cop-select" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
						{sig.options.map((o) => (
							<option key={o.value} value={o.value}>{o.label}</option>
						))}
					</select>
				)}
			</div>
		</div>
	);
}

export default function ExitIntelligence() {
	const { isInternal, reportDateRangeLabel } = useCop();
	const [aggressiveness, setAggressiveness] = useState(5);
	const [signals, setSignals] = useState(DEFAULT_PURCHASE_SIGNALS);

	const preset = aggressivenessPresets[aggressiveness];
	const { score, rows } = useMemo(() => computePurchaseProbability(signals), [signals]);
	const impactByAttribute = useMemo(() => Object.fromEntries(rows.map((r) => [r.attribute, r])), [rows]);

	const setSignal = (attribute, value) => setSignals((prev) => ({ ...prev, [attribute]: value }));
	const resetSignals = () => setSignals(DEFAULT_PURCHASE_SIGNALS);

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 280 }}>
					<h1 className="cop-page-title">
						Exit Intelligence <Pill tone="green">live</Pill>
					</h1>
					<div className="cop-page-sub">
						{account.merchant_name} · {reportDateRangeLabel} · detection engine: {engine}
					</div>
				</div>
			</div>

			<div className="cop-stats-row">
				{stats.map((s) => (
					<StatCard key={s.label} label={s.label} value={s.value} trend={s.trend} tone="pink" />
				))}
			</div>

			<div className="cop-grid cop-builder-rules" style={{ marginTop: 16 }}>
				<Card
					title="Purchase Probability Model"
					icon="◎"
					right={<button className="cop-link" onClick={resetSignals}>Reset signals</button>}
				>
					<div className="cop-exit-gauge">
						<div className="cop-exit-gauge-track">
							<div className="cop-exit-gauge-fill" style={{ width: `${score}%` }} />
							<div className="cop-exit-gauge-mark" style={{ left: `${score}%` }}>{score}%</div>
						</div>
					</div>
					<p style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6, margin: '14px 0 6px' }}>{purchaseSummary}</p>
					<div className="cop-exit-signals">
						{PURCHASE_SIGNALS.map((sig) => {
							const row = impactByAttribute[sig.attribute] || { impact: '0.0%', tone: 'amber' };
							return (
								<SignalRow
									key={sig.attribute}
									sig={sig}
									value={signals[sig.attribute]}
									impact={row.impact}
									tone={row.tone}
									onChange={(v) => setSignal(sig.attribute, v)}
								/>
							);
						})}
					</div>
				</Card>

				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Card title="Exit Detection" icon="⚙" right={<span className="cop-mono">{preset.label}</span>}>
						<div className="cop-conf-setting">
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
								<span style={{ fontSize: 12.5, color: 'var(--text-sec)' }}>Aggressiveness · level {aggressiveness} of 10</span>
								<span className="cop-num tone-pink" style={{ fontSize: 18 }}>{aggressiveness}</span>
							</div>
							<input
								type="range"
								min="1"
								max="10"
								step="1"
								value={aggressiveness}
								onChange={(e) => setAggressiveness(Number(e.target.value))}
								aria-label="Detection aggressiveness"
							/>
							<div className="cop-conf-setting-hint">
								Lower levels wait for stronger exit signals before launching; higher levels launch sooner and more often. Each
								parameter below is derived from this dial.
							</div>
						</div>

						<div className="cop-exit-params">
							{exitParams.map((param) => (
								<div className="cop-exit-param" key={param.key}>
									<div className="cop-exit-param-head">
										<span className="cop-exit-param-label">{param.label}</span>
										<span className="cop-exit-param-value cop-num">{preset[param.key]}</span>
									</div>
									<div className="cop-exit-param-zone">{param.zone}</div>
									<div className="cop-exit-param-assoc">{param.assoc}</div>
								</div>
							))}
						</div>

						<div className="cop-exit-safeguards">
							<span className="cop-exit-safeguards-label">Always-on safeguards</span>
							{alwaysOnSafeguards.map((s) => (
								<div className="cop-exit-safeguard" key={s.label}>
									<b>{s.label}</b> — {s.desc}
								</div>
							))}
						</div>
					</Card>

					{isInternal && (
						<InternalCard title="Engine Diagnostics" icon="⚗" right={diagnostics.engine}>
							{diagnostics.warnings.map((w, i) => (
								<WarnRow key={i} level={w.level}>{w.text}</WarnRow>
							))}
							<div className="cop-aop-links">
								<AopLink view="workflow-runs">exit_monitor telemetry</AopLink>
								<AopLink view="audit-log">launch suppression log</AopLink>
							</div>
						</InternalCard>
					)}
				</div>
			</div>
		</main>
	);
}
